// Story's left column (StoryRoom.swift's `StoryColumn`): the people, most present in the manuscript first, in their
// colours (each opens their character study); the places, then objects and motifs (each opens Cast & Places); then
// "+ Add a character or place" and "Suggest names…" (the name generator). Every Story page brings it as its column.
import { useState } from 'react';
import type { PageProps } from '../../shell/ProjectShell.tsx';
import { followLink, navigate } from '../../shell/route.ts';
import { opened, storyModel } from '../../store/story.ts';
import { EntityDot, plural } from './common.tsx';
import { NameGenerator } from './NameGenerator.tsx';

export function StoryColumn(props: PageProps) {
  const { project, hrefFor, params, page } = props;
  const [naming, setNaming] = useState(false);
  const model = storyModel(project);
  const entities = opened(project).record.entities;
  const places = entities.filter((e) => e.kind === 'place');
  const things = entities.filter((e) => e.kind === 'object' || e.kind === 'motif');
  const studied = page.page === 'study' ? (params.who && entities.some((e) => e.id === params.who) ? params.who : model.people[0]?.id) : null;
  const row = (id: string, name: string, to: 'plan/study' | 'plan/cast', leading: React.ReactNode, detail: string | null, selected: boolean) => {
    const href = hrefFor(to, null, { who: id });
    return (
      <li key={id}>
        <a className={`column-row entity-row${selected ? ' selected' : ''}`} href={href} aria-current={selected ? 'true' : undefined} data-entity-id={id} onClick={(e) => followLink(e, href)}>
          {leading}
          <span className="story-column-name">
            <span className="column-row-title">{name}</span>
            {detail ? <span className="column-row-detail">{detail}</span> : null}
          </span>
        </a>
      </li>
    );
  };
  return (
    <div className="story-column" data-story-column>
      <section className="column-group" aria-label="People">
        <h2 className="eyebrow column-eyebrow">People</h2>
        {model.people.length ? null : <p className="column-note">No characters yet. Type @ and a name in Write, or add them in Cast &amp; Places.</p>}
        <ul className="column-list">
          {model.people.map((p) =>
            row(p.id, p.name, 'plan/study', <EntityDot color={p.color} />, plural(model.appearances.get(p.id)?.scenes ?? 0, 'scene'), studied === p.id),
          )}
        </ul>
      </section>
      {places.length ? (
        <section className="column-group" aria-label="Places">
          <h2 className="eyebrow column-eyebrow">Places</h2>
          <ul className="column-list">{places.map((p) => row(p.id, p.name, 'plan/cast', null, null, page.page === 'cast' && params.who === p.id))}</ul>
        </section>
      ) : null}
      {things.length ? (
        <section className="column-group" aria-label="Objects and motifs">
          <h2 className="eyebrow column-eyebrow">Objects and motifs</h2>
          <ul className="column-list">{things.map((t) => row(t.id, t.name, 'plan/cast', null, null, page.page === 'cast' && params.who === t.id))}</ul>
        </section>
      ) : null}
      <p className="story-column-actions">
        <a href={hrefFor('plan/cast', null)} onClick={(e) => followLink(e, hrefFor('plan/cast', null))}>
          + Add a character or place
        </a>
        <button type="button" className="link-button" onClick={() => setNaming(true)}>
          Suggest names…
        </button>
      </p>
      {naming ? (
        <NameGenerator
          project={project}
          live={props.live}
          onClose={() => setNaming(false)}
          onShow={(id, study) => {
            setNaming(false);
            navigate(hrefFor(study ? 'plan/study' : 'plan/cast', null, { who: id }));
          }}
        />
      ) : null}
    </div>
  );
}
